import { TypographyV3 } from '@jouzen/ecom-components';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useConfig } from 'statsig-react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';
import BackdropBlur from '@/app/components/BackdropBlur';
import Image, { type ImageFormat } from '@/app/components/Image';
import type { MotionProps } from '@/app/components/Motion';
import Motion from '@/app/components/Motion';

interface RenderData {
  additionalSegmentProperties: {
    cta: string;
    location: string;
    action: string;
  };
  ctaLocaleKey: MessageKey;
  image: {
    altKey: MessageKey;
    src: string;
    format: ImageFormat;
  };
  localeKey: MessageKey;
  target: string;
  rel: string;
  url: string;
}

interface HomeHeroShopChicletProps {
  readonly onHeroButtonFocus: (
    event: React.FocusEvent<HTMLAnchorElement>,
    topHero: boolean | undefined,
  ) => void;
  readonly topHero?: boolean;
}

const HomeHeroShopChiclet = ({
  onHeroButtonFocus,
  topHero,
}: HomeHeroShopChicletProps): JSX.Element | null => {
  const t = useTranslations();
  const path = usePathname();
  const { config } = useConfig('home_hero_chiclet');
  const activeChicletConfig = Object.keys(config.value).length > 0;
  const value = config.value as unknown as RenderData;
  const renderData = activeChicletConfig && value;

  const handleClick = async (): Promise<void> => {
    if (!renderData) return;
    const { action, cta, location } = renderData.additionalSegmentProperties;
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta,
      action,
      location,
      path,
    });
  };

  const motionProps: MotionProps = {
    animate: {
      opacity: 1,
    },
    initial: {
      opacity: 0,
    },
    transition: {
      delay: 1,
      duration: 0.25,
      type: 'just',
    },
  };

  if (!renderData) return null;

  return (
    <Link
      href={renderData.url}
      className="mb-6 inline-block w-full lg:mb-8"
      data-cy="home-hero-shop-chiclet"
      rel={renderData.rel}
      target={renderData.target}
      onClick={handleClick}
      onFocus={(event) => onHeroButtonFocus(event, topHero)}
    >
      <div className="relative z-100 p-1">
        <BackdropBlur
          {...motionProps}
          color={{ sm: 'dark', lg: 'dark' }}
          opacity={{ sm: 0.4, lg: 0.4 }}
          variant="chiclet"
        />
        <Motion className="relative flex" {...motionProps}>
          <div className="relative aspect-2/1 overflow-hidden rounded-xl px-20">
            <Image
              alt={t(renderData.image.altKey)}
              className="object-cover"
              fill
              format={renderData.image.format}
              priority
              sizes="200px"
              src={renderData.image.src}
            />
          </div>
          <div className="flex flex-col justify-between p-4">
            <TypographyV3
              className="mb-4 lg:mb-6"
              color="light"
              variant="body-small"
            >
              {t.rich(renderData.localeKey)}
            </TypographyV3>
            <TypographyV3 color="light" variant="body-small" weight="bold">
              {t(renderData.ctaLocaleKey)}
            </TypographyV3>
          </div>
        </Motion>
      </div>
    </Link>
  );
};

export default HomeHeroShopChiclet;
